import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import { GraphqlQueryContext } from '@interfaces/graphql';
import {
  BioinformaticReportQueriesFindAllArgs,
  BioinformaticReportQueryResult,
  Query
} from '@interfaces/schema';
import { WebStorageReportOptions } from '@interfaces/web-storage';
import { Session } from '@providers/session/session.service';

/**
 * Trait Service.
 *
 * @exports
 * @class ReportViewTraitService
 */
@Injectable()
export class ReportViewTraitService {
  private readonly apollo = inject(Apollo);
  private readonly session = inject(Session);

  /**
   * Get context.
   *
   * @returns {GraphqlQueryContext}
   * @memberof ReportViewTraitService
   */
  get context(): GraphqlQueryContext {
    return {
      contentLanguage: this.language
    };
  }

  /**
   * Get active or selected language.
   *
   * @returns {string}
   * @memberof ReportViewTraitService
   */
  get language(): string | undefined {
    return this.options?.language;
  }

  /**
   * Get option report.
   *
   * @returns {WebStorageReportOptions}
   * @memberof ReportViewTraitService
   */
  get options(): WebStorageReportOptions | null {
    return this.session.retrieve(this.session.keys.reportNourishOptions) ?? {};
  }

  /**
   * FindAllBiReports.
   *
   * @param {BioinformaticReportQueriesFindAllArgs} variables BioinformaticReportQueriesFindAllArgs.
   * @returns {Observable<BioinformaticReportQueryResult>}
   */
  findAllBiReports(
    variables: BioinformaticReportQueriesFindAllArgs
  ): Observable<BioinformaticReportQueryResult> {
    const query = gql`
      query findAllReport(
        $where: BioinformaticReportFilter
        $sort: BioinformaticReportQuerySortBy
        $limit: Int
        $offset: Int
      ) {
        Bioinformatic {
          Report {
            findAll(
              where: $where
              sort: $sort
              limit: $limit
              offset: $offset
            ) {
              count
              results {
                id
                score
                dnaScoreTypeGuideline {
                  id
                  mappedScore
                  response
                  riskLevel
                  dScoreType {
                    id
                    name
                    isHigherBetter
                  }
                  dnaScoreTypeId
                }
                panel {
                  id
                  name
                  thumbnail
                }
                genotypes {
                  gene {
                    id
                    name
                    description
                  }
                  types
                }
                traitScore {
                  id
                  rating
                  minScore: from
                  maxScore: to
                  description: interpretation
                  shortInterpretation
                  symptoms
                  dos
                  donts
                }
                trait {
                  id
                  name
                  description
                }
                category {
                  id
                  name
                }
                dnaTrait {
                  id
                  name
                  description
                  guidelineType
                }
                dnaCategory {
                  id
                  name
                  thumbnail
                }
                dnaSubCategory {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;

    return this.apollo
      .query<Query>({
        query,
        variables,
        context: this.context
      })
      .pipe(
        map(
          ({ data }) =>
            data?.Bioinformatic?.Report
              ?.findAll as BioinformaticReportQueryResult
        )
      );
  }
}
